import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/axios';

export const AuthContext = createContext();

export const useAuth = () => React.useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshIntervalRef = useRef(null);

  // Verificar si hay un usuario autenticado al cargar la aplicación
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      
      if (token) {
        try {
          // Verificar si el token es válido
          const response = await api.get('/usuarios/me/');
          setCurrentUser(response.data);
        } catch (error) {
          console.error('Error al verificar autenticación:', error);
          // Si el token no es válido, limpiar el localStorage
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          setCurrentUser(null);
        }
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Función para iniciar sesión
  const login = async (credentials) => {
    try {
      const response = await api.post('/usuarios/auth/login/', credentials);
      const { access, refresh, user } = response.data;
      
      // Guardar ambos tokens
      localStorage.setItem('authToken', access);
      localStorage.setItem('refreshToken', refresh);
      setCurrentUser(user);
      setError(null);
      
      return user;
    } catch (error) {
      setError('Credenciales inválidas');
      throw error;
    }
  };

  // Función para establecer usuario y tokens sin llamada adicional (registro)
  const loginWithTokens = ({ access, refresh, user }) => {
    if (access) localStorage.setItem('authToken', access);
    if (refresh) localStorage.setItem('refreshToken', refresh);
    setCurrentUser(user || null);
    setError(null);
  };

  // Función para cerrar sesión (memoizada)
  const logout = useCallback(() => {
    // Limpiar flag de onboarding si existe para el usuario actual
    if (currentUser?.id) {
      sessionStorage.removeItem(`onboarding_dismissed_${currentUser.id}`);
    }

    // Limpiar TODOS los tokens y storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('csrfToken');
    localStorage.removeItem('auth-storage'); // Limpiar cualquier residuo de Zustand
    
    setCurrentUser(null);
    
    // Limpiar el intervalo de renovación
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, [currentUser]);

  // Función para renovar token automáticamente (memoizada)
  const refreshTokenAutomatically = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return;

      const response = await api.post('/usuarios/auth/refresh/', {
        refresh: refreshToken
      });

      const { access, refresh } = response.data;
      localStorage.setItem('authToken', access);
      if (refresh) {
        localStorage.setItem('refreshToken', refresh);
      }
      
      // Token renovado automáticamente
    } catch (error) {
      console.error('Error al renovar token automáticamente:', error);
      // Si falla la renovación automática, cerrar sesión
      logout();
    }
  }, [logout]);

  // Configurar renovación automática de tokens
  useEffect(() => {
    if (currentUser) {
      // Renovar token cada 7 horas (antes de que expire en 8 horas)
      refreshIntervalRef.current = setInterval(refreshTokenAutomatically, 7 * 60 * 60 * 1000);
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [currentUser, refreshTokenAutomatically]);

  // Función para refrescar los datos del usuario manualmente (memoizada para evitar loops)
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/usuarios/me/');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error al refrescar usuario:', error);
    }
  }, []);

  const value = {
    currentUser,
    loading,
    error,
    login,
    loginWithTokens,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};